import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface FavoritesSidebarContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const FavoritesSidebarContext = createContext<FavoritesSidebarContextType | undefined>(undefined);

export const FavoritesSidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'favorites' } }));
  }, []);

  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'favorites') {
        setIsOpen(false);
      }
    };
    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, []);

  return (
    <FavoritesSidebarContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </FavoritesSidebarContext.Provider>
  );
};

export const useFavoritesSidebar = () => {
  const context = useContext(FavoritesSidebarContext);
  if (!context) {
    throw new Error('useFavoritesSidebar must be used within FavoritesSidebarProvider');
  }
  return context;
};
